import {
    activityCategories,
    ActivityCategory,
    ActivityRecordType,
    MarketingGoalTargets,
    OutcomeGoalMetric,
    outcomeGoalMetrics,
    outcomeGoalMetricShortLabels,
    TaskTypeId,
} from "@sp-crm/core";
import {
    ActivityTypeOption,
    activityTypesForRecordType,
} from "components/tasks/activity-type-options";
import { activityCategoryBarColor, outcomeGoalMetricBarColor } from "./pace";

export const marketingGoalRecordType: ActivityRecordType = "referenceContact";

export const goalActivityTypeOptions = (
    options: ActivityTypeOption[],
    referencedTaskTypeIds: Set<string>,
): ActivityTypeOption[] =>
    activityTypesForRecordType(
        options,
        marketingGoalRecordType,
        Array.from(referencedTaskTypeIds) as TaskTypeId[],
    );

export type GoalColumn =
    | {
          kind: "activity";
          taskTypeId: TaskTypeId;
          label: string;
          category: ActivityCategory;
      }
    | { kind: "outcome"; metric: OutcomeGoalMetric; label: string };

export type ActivityGoalColumn = Extract<GoalColumn, { kind: "activity" }>;

export const isActivityGoalColumn = (column: GoalColumn): column is ActivityGoalColumn =>
    column.kind === "activity";

export const goalColumnKey = (column: GoalColumn): string =>
    column.kind === "activity" ? column.taskTypeId : column.metric;

const categoryOrder = (category: ActivityCategory): number =>
    activityCategories.indexOf(category);

export interface GoalColumnOrdering {
    category: ActivityCategory;
    label: string;
}

export const compareGoalColumnOrder = (
    a: GoalColumnOrdering,
    b: GoalColumnOrdering,
): number =>
    categoryOrder(a.category) - categoryOrder(b.category) ||
    a.label.toLocaleLowerCase().localeCompare(b.label.toLocaleLowerCase());

export interface GoalRow {
    goal?: MarketingGoalTargets | null;
}

export const hasGoalTargets = (row: GoalRow): boolean =>
    !!row.goal &&
    (row.goal.referrals > 0 ||
        row.goal.careStarts > 0 ||
        Object.values(row.goal.activity).some(target => target > 0));

export const goalTargetedActivityTypeIds = (rows: GoalRow[]): Set<string> => {
    const ids = new Set<string>();
    for (const row of rows) {
        for (const [taskTypeId, target] of Object.entries(row.goal?.activity ?? {})) {
            if (target > 0) {
                ids.add(taskTypeId);
            }
        }
    }
    return ids;
};

export const buildGoalColumns = (
    options: ActivityTypeOption[],
    rows: GoalRow[],
): GoalColumn[] => {
    const targeted = goalTargetedActivityTypeIds(rows);
    const activityColumns: GoalColumn[] = options
        .filter(option => targeted.has(option.id))
        .map(option => ({
            kind: "activity" as const,
            taskTypeId: option.id,
            label: option.name,
            category: option.category,
        }))
        .sort(compareGoalColumnOrder);
    const outcomeColumns: GoalColumn[] = outcomeGoalMetrics
        .filter(metric => rows.some(row => (row.goal?.[metric] ?? 0) > 0))
        .map(metric => ({
            kind: "outcome",
            metric,
            label: outcomeGoalMetricShortLabels[metric],
        }));
    return [...outcomeColumns, ...activityColumns];
};

export const goalColumnValue = (
    targets: MarketingGoalTargets,
    column: GoalColumn,
): number =>
    column.kind === "activity"
        ? (targets.activity[column.taskTypeId] ?? 0)
        : targets[column.metric];

export const goalColumnBarColor = (column: GoalColumn): string =>
    column.kind === "activity"
        ? activityCategoryBarColor[column.category]
        : outcomeGoalMetricBarColor[column.metric];
